import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Animated, Text} from 'react-native';
import {useTheme} from 'styled-components/native';

import analytics from '@react-native-firebase/analytics';

import {
  ActionChipGhost,
  ProfileButton,
  ChipIconBox,
  ChipIconSolid,
  ChipIconGhost,
  ChipLabelGhost,
  Row,
  AccountBalance,
  AccountBalanceColumn,
  AccountBalanceDivider,
  AccountBalanceTitle,
  AccountBalanceArrow,
  AccountBalanceValue,
  AccountBalanceRow,
  PeopleIcon,
  AccountBalanceButton,
} from './styles';

import {api} from '../../../../services/api';
import {useAuth} from '../../../../context/auth';

import {useGlobal} from '../../../../context/global';

import {SafeAreaView} from 'react-native-safe-area-context';
import {useCarrinhos} from '../../../../context/useCarrinhos';

import {Baratinhaz} from '../../Baratinhaz';
import {RFValue} from 'react-native-responsive-fontsize';

const COLLAPSE_DISTANCE = 300;

const BOTTOM_SECTION_HEIGHT = RFValue(55) + RFValue(0);

interface GreenHeaderProps {
  scrollY: Animated.Value;
}

export function GreenHeader({scrollY}: GreenHeaderProps) {
  const navigation = useNavigation();
  const {isLogged, setShowSign} = useAuth();
  const isFocused = useIsFocused();
  const {loadDisabledHighlights} = useGlobal();
  const {
    currentCarIndex,
  } = useCarrinhos();
  const theme = useTheme();

  const [baratinhas, setBaratinhas] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [indicationsCount, setIndicationsCount] = useState(0);

  const LABEL_ITEMS = [
    <Text key="label-1">
      {'Ganhe '}
      <Text style={{fontFamily: theme.fonts.bold}}>DINHEIRO!</Text>
    </Text>,
    <Text key="label-2">
      <Text style={{fontFamily: theme.fonts.bold}}>$$</Text>
      {' por indicação'}
    </Text>,
  ];
  const LABEL_DISPLAY_DURATION = 3500;
  const LABEL_ANIMATION_DURATION = 300;

  const labelAnim = useRef(new Animated.Value(0)).current;
  const labelAnimRef = useRef<Animated.CompositeAnimation | null>(null);
  const [labelIndex, setLabelIndex] = useState(0);

  useEffect(() => {
    labelAnimRef.current?.stop();
    labelAnim.setValue(0);

    const sequence = Animated.sequence([
      Animated.timing(labelAnim, {
        toValue: 1,
        duration: LABEL_ANIMATION_DURATION,
        useNativeDriver: true,
      }),
      // Animated.delay usa useNativeDriver: false internamente (fixo no
      // react-native), o que cria um interaction handle a cada ciclo. Como
      // esse efeito se retrigger sozinho (troca de label reagenda o mesmo
      // efeito), isso rodava pra sempre e travava o InteractionManager. Um
      // timing "parado" no mesmo valor com useNativeDriver: true produz a
      // mesma espera sem esse efeito colateral.
      Animated.timing(labelAnim, {
        toValue: 1,
        duration: LABEL_DISPLAY_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(labelAnim, {
        toValue: 2,
        duration: LABEL_ANIMATION_DURATION,
        useNativeDriver: true,
      }),
    ]);

    labelAnimRef.current = sequence;
    labelAnimRef.current.start(({finished}) => {
      if (!finished) {
        return;
      }
      setLabelIndex(prev => (prev + 1) % LABEL_ITEMS.length);
    });

    return () => labelAnimRef.current?.stop();
  }, [labelAnim, labelIndex]);

  const labelTranslateY = labelAnim.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [8, 0, -8],
  });

  const labelOpacity = labelAnim.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [0, 1, 0],
  });

  useEffect(() => {
    let isMounted = true;

    async function fetchUsers() {
      setIsLoading(true);
      await api
        .post('/indiqueGanhe')
        .then(response => {
          if (isMounted) {
            setIndicationsCount(response.data.qtde_indicados);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
            isMounted = false;
          }
        });
    }

    if (isLogged) {
      fetchUsers();
    } else {
      if (isMounted) {
        setIndicationsCount(0);
        setIsLoading(false);
        isMounted = false;
      }
    }

    loadDisabledHighlights();

    return () => {
      isMounted = false;
    };
  }, [isFocused, isLogged]);

  function handleHelp() {
    analytics().logEvent('header_verde_clica_icone_ajuda');
    navigation.navigate('Help');
  }
  function handleUser() {
    analytics().logEvent('header_verde_clica_perfil');
    navigation.navigate('User');
  }

  function handleIndicados() {
    if (!isLogged) {
      return setShowSign('signup');
    }
    navigation.navigate('ReferAndWinUsers');
  }

  function handleBaratinhaz() {
    if (!isLogged) {
      return setShowSign(true);
    }

    navigation.navigate('BaratinhasAjuda');
  }

  useEffect(() => {
    let isMounted = true;

    async function buscaBaratinhas() {
      setIsLoading(true);
      await api
        .get('/consultaBaratinhas')
        .then(res => {
          if (isMounted) {
            setBaratinhas(res.data);
          }
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
            isMounted = false;
          }
        });
    }

    if (isLogged) {
      buscaBaratinhas();
    } else {
      if (isMounted) {
        setBaratinhas(0);
        setIsLoading(false);
        isMounted = false;
      }
    }

    return () => {
      isMounted = false;
    };
  }, [isFocused, isLogged]);

  const {refetch} = useCarrinhos();

  useFocusEffect(
    useCallback(() => {
      if (!isLogged) {
        return;
      }
      refetch();
    }, [isLogged]),
  );

  const progress = scrollY.interpolate({
    inputRange: [0, COLLAPSE_DISTANCE],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  const bottomMaxHeight = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [BOTTOM_SECTION_HEIGHT, 0],
  });

  const collapsibleOpacity = progress.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0.3, 0],
  });


  function handleNavigateToAchievements(){
    if(!isLogged){
      setShowSign(true);
    }else{
      navigation.navigate('Achievements');
    }
  }

  return (
    <SafeAreaView
      edges={['top']}
      style={{
        backgroundColor: theme.colors.primary,
        borderBottomLeftRadius: RFValue(40),
        borderBottomRightRadius: RFValue(40),
        zIndex: 1,
      }}>
      <Animated.View
        style={{paddingHorizontal: 24, paddingBottom: RFValue(35), zIndex: 2}}>
        {/* Collapsible: chips */}
        <Animated.View>
          <Row>
            <Row>
              <ProfileButton onPress={handleUser} activeOpacity={0.7}>
                <ChipIconSolid name="gear" />
              </ProfileButton>
            </Row>

            <Row>
              <ActionChipGhost onPress={handleHelp} activeOpacity={0.7}>
                <ChipIconBox>
                  <ChipIconGhost name="question-circle" />
                </ChipIconBox>
                <ChipLabelGhost>Ajuda</ChipLabelGhost>
              </ActionChipGhost>
              <ActionChipGhost
                style={{backgroundColor: theme.colors.achievements_color}}
                onPress={handleNavigateToAchievements}
                activeOpacity={0.7}>
                <ChipIconBox>
                  <ChipIconGhost
                    name="trophy"
                    style={{color: theme.colors.shape}}
                  />
                </ChipIconBox>
                <ChipLabelGhost style={{color: theme.colors.shape}}>
                  Nível {currentCarIndex + 1}
                </ChipLabelGhost>
              </ActionChipGhost>
            </Row>
          </Row>
        </Animated.View>

        {/* Collapsible: saldo + menus */}
        <Animated.View
          style={{
            maxHeight: bottomMaxHeight,
            opacity: collapsibleOpacity,
            overflow: 'hidden',
          }}>
          <AccountBalance>
            <AccountBalanceButton
              onPress={handleBaratinhaz}
              activeOpacity={0.7}>
              <AccountBalanceColumn>
                <AccountBalanceTitle>
                  {isLogged ? 'BaratinhaZ' : 'Já é cliente?'}
                </AccountBalanceTitle>
                <AccountBalanceRow>
                  {isLogged && <Baratinhaz />}
                  {isLogged ? (
                    <AccountBalanceValue>
                      {isLoading ? '••••' : baratinhas} btz
                    </AccountBalanceValue>
                  ) : (
                    <AccountBalanceValue>Fazer Login</AccountBalanceValue>
                  )}
                </AccountBalanceRow>
              </AccountBalanceColumn>
              <AccountBalanceArrow name="chevron-right" />
            </AccountBalanceButton>
            <AccountBalanceDivider />
            <AccountBalanceButton onPress={handleIndicados} activeOpacity={0.7}>
              <AccountBalanceColumn>
                {isLogged ? (
                  <Animated.View
                    style={{
                      transform: [{translateY: labelTranslateY}],
                      opacity: labelOpacity,
                    }}>
                    <AccountBalanceTitle>
                      {LABEL_ITEMS[labelIndex]}
                    </AccountBalanceTitle>
                  </Animated.View>
                ) : (
                  <AccountBalanceTitle>Não é cliente?</AccountBalanceTitle>
                )}
                <AccountBalanceRow>
                  {isLogged && <PeopleIcon name="users" />}
                  {isLogged ? (
                    <AccountBalanceValue>
                      {isLoading ? '••••' : indicationsCount}
                    </AccountBalanceValue>
                  ) : (
                    <AccountBalanceValue>Cadastre-se</AccountBalanceValue>
                  )}
                </AccountBalanceRow>
              </AccountBalanceColumn>
              <AccountBalanceArrow name="chevron-right" />
            </AccountBalanceButton>
          </AccountBalance>

        </Animated.View>
      </Animated.View>
    </SafeAreaView>
  );
}
